import CURRENCIES from "./currencies.json";
import { t } from "../../../utils/i18n";
import CryptoJS from "crypto-js";
import { postRequest } from "../../../utils/httpRequest";

// Constants for encryption
const ENCRYPTION_KEY =
  process.env.NEXT_PUBLIC_ENCRYPTION_KEY || "fallback-encryption-key";

export const formatCurrency = (amount, currencyCode) => {
  if (!amount) return "";

  try {
    // Format based on currency
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(amount);
  } catch (error) {
    console.error("Currency formatting error:", error);
    // Fallback formatting
    return `${getCurrencySymbol(currencyCode)}${parseFloat(amount).toFixed(2)}`;
  }
};

export const getCurrencySymbol = (currencyCode) => {
  return CURRENCIES[currencyCode]?.symbol || currencyCode;
};

export const calculateUsdAmount = (amount, currency, exchangeRates) => {
  if (!amount || currency === "USD") return amount;

  const rate = exchangeRates[currency] || 1;
  // Convert to USD (divide by the exchange rate)
  return (parseFloat(amount) / rate).toFixed(2);
};

export const encryptApiKey = (apiKey) => {
  try {
    if (!apiKey) {
      throw new Error("API key is required for encryption");
    }
    return CryptoJS.AES.encrypt(apiKey, ENCRYPTION_KEY).toString();
  } catch (error) {
    console.error("Encryption error:", error);
    throw new Error("Failed to encrypt API key");
  }
};

export const decryptApiKey = (encryptedKey) => {
  try {
    if (!encryptedKey) {
      throw new Error("Encrypted key is required for decryption");
    }
    const bytes = CryptoJS.AES.decrypt(encryptedKey, ENCRYPTION_KEY);
    const decryptedKey = bytes.toString(CryptoJS.enc.Utf8);

    if (!decryptedKey) {
      throw new Error("Decryption failed - invalid key or corrupted data");
    }

    return decryptedKey;
  } catch (error) {
    console.error("Decryption error:", error);
    throw new Error("Failed to decrypt API key");
  }
};

export const validatePaymentForm = (formData) => {
  const errors = {};

  // Required fields validation
  if (
    formData.amount === undefined ||
    formData.amount === null ||
    formData.amount === "" ||
    isNaN(formData.amount) ||
    parseFloat(formData.amount) < 0
  ) {
    errors.amount = t("store_integrations.widget.valid_amount");
  }

  if (!formData.currency) {
    errors.currency = t("store_integrations.widget.select_currency");
  }

  if (!formData.description?.trim()) {
    errors.description = t("store_integrations.widget.enter_description");
  }

  if (!formData.product_name?.trim()) {
    errors.product_name = t("store_integrations.widget.enter_product_name");
  }

  // GoHighLevel validation
  if (formData.gohighlevel_enabled) {
    if (!formData.gohighlevel_api_key?.trim()) {
      errors.gohighlevel_api_key =
        t("store_integrations.widget.ghl_key_required");
    } else {
      try {
        // Test encryption of the API key
        const encrypted = encryptApiKey(formData.gohighlevel_api_key);
        // Test decryption to ensure it works
        const decrypted = decryptApiKey(encrypted);
        if (decrypted !== formData.gohighlevel_api_key) {
          errors.gohighlevel_api_key = t("store_integrations.widget.invalid_api_key");
        }
      } catch (error) {
        errors.gohighlevel_api_key = t("store_integrations.widget.invalid_api_key");
      }
    }
  }

  // Custom tags validation
  if (formData.custom_tags) {
    const invalidTags = formData.custom_tags.filter((tag) => {
      if (!tag) return false; // Empty tags are allowed
      return !/^[a-zA-Z0-9\s-]+$/.test(tag);
    });

    if (invalidTags.length > 0) {
      errors.custom_tags =
        t("store_integrations.widget.invalid_tags");
    }
  }

  return errors;
};

// Helper function to format product tags
export const formatProductTag = (name) => {
  if (typeof name !== "string") return "";
  return name
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase();
};

export const generatePaymentLink = (
  baseUrl,
  params,
  storeId,
  walletAddress,
  exchangeRates,
) => {
  // Create URL parameters
  const urlParams = new URLSearchParams({
    store_id: storeId,
    amount: params.amount,
    currency: params.currency,
    description: params.description,
    redirect_url: params.redirect_url,
    usd_amount: params.usd_amount,
  });

  // Add wallet address if provided
  if (walletAddress) {
    urlParams.append("wallet_address", walletAddress);
  }

  // Add exchange rate if available and currency is not USD
  if (
    params.currency !== "USD" &&
    exchangeRates &&
    exchangeRates[params.currency]
  ) {
    urlParams.append("exchange_rate", exchangeRates[params.currency]);
  }

  // Construct the final URL
  return `${baseUrl}?${urlParams.toString()}`;
};

export const copyToClipboard = (text, setCopied) => {
  navigator.clipboard.writeText(text);
  setCopied(true);
  setTimeout(() => setCopied(false), 2000);
};

export const truncateAddress = (address) => {
  if (!address || address.length <= 13) return address;
  return `${address.substr(0, 6)}...${address.substr(-4)}`;
};

export const sanitizeInput = (inputString) => {
  if (typeof inputString !== "string") return "";
  return inputString.replace(/[^a-zA-Z0-9\s\-_.,()&]/g, "");
};

export const shortenUrl = async (originalUrl) => {
  try {
    const data = await postRequest("/api/v1/shorten_urls", {
      original_url: originalUrl,
    });

    // Return the shortened URL or fallback to original
    return data.shortened_url || data.short_url || originalUrl;
  } catch (error) {
    console.error("Error shortening URL:", error);
    return originalUrl;
  }
};

export const generatePaymentUrl = async (params) => {
  const baseUrl = "https://payment.bananacrystal.com/pay";
  const urlParams = new URLSearchParams();

  // Add all parameters to the URL
  Object.keys(params).forEach((key) => {
    if (
      params[key] !== undefined &&
      params[key] !== null &&
      params[key] !== ""
    ) {
      urlParams.append(key, params[key]);
    }
  });

  const fullPaymentUrl = `${baseUrl}?${urlParams.toString()}`;

  // Shorten the URL before returning
  return await shortenUrl(fullPaymentUrl);
};
